import type {
  OperationExecutionEventStatus,
  OperationExecutionGraph,
  OperationExecutionKind,
  OperationExecutionRefs,
  OperationExecutionState,
  OperationExecutionViewKind,
  OperationExecutionWithdrawRef,
} from "@repo/ports/v2";
import {
  EXECUTION_ACTIVITY_EVENT_KINDS,
  EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN,
  EXECUTION_ACTIVITY_PHASES,
} from "@repo/ports/v2/operation-execution/activity-semantics";
import { z } from "zod";
import {
  ExpectedMarketOrderVenueSchema,
  LifiMarketOrderVenueKeySchema,
  NonNegativeIntegerAmountSchema,
  PositiveIntegerAmountSchema,
  SolanaAddressSchema,
  SpecDrivenBuyPlanBasisSchema,
} from "./requests.js";
import { lazySchema } from "@repo/lazy-schema";

const JsonPrimitiveSchema = lazySchema(() =>
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);

type JsonPrimitive = string | number | boolean | null;
export type ExecutionLiveJsonValue =
  | JsonPrimitive
  | readonly ExecutionLiveJsonValue[]
  | { readonly [key: string]: ExecutionLiveJsonValue };

export type ExecutionLiveRefs = Omit<
  OperationExecutionRefs,
  | "executionIntent"
  | "withdraw"
  | "workflowParams"
  | "workflowVersion"
  | "convergeRestartAttempts"
  | "convergeResumes"
> & {
  readonly executionIntent?: never;
  readonly workflowParams?: never;
  readonly workflowVersion?: never;
  readonly convergeRestartAttempts?: never;
  readonly convergeResumes?: never;
  readonly withdraw?:
    | (Omit<OperationExecutionWithdrawRef, "recipientAddress"> & {
        readonly recipientAddress?: string;
      })
    | null;
} & {
  // `| undefined` keeps the forward-compat catchall compatible with the
  // declared optional properties above: without it no object with an absent
  // optional ref is assignable to this type under strict index-signature
  // checking (webapp builds construct refs objects by spreading partials).
  readonly [key: string]: ExecutionLiveJsonValue | undefined;
};

export const ExecutionLiveJsonValueSchema: z.ZodType<ExecutionLiveJsonValue> =
  lazySchema(() =>
    z.lazy(() =>
      z.union([
        JsonPrimitiveSchema,
        z.array(ExecutionLiveJsonValueSchema),
        z.record(z.string(), ExecutionLiveJsonValueSchema),
      ]),
    ),
  );

export const ExecutionLiveJsonObjectSchema = lazySchema(() =>
  z.record(z.string(), ExecutionLiveJsonValueSchema),
);

export const ExecutionStateSchema = lazySchema(() =>
  z.enum([
    "accepted",
    "running",
    "awaiting_user",
    "retrying",
    "completed",
    "failed",
    "cancelled",
  ]),
);

/**
 * The ORDER's onchain fill, published before indexer settlement completes.
 *
 * Present only for single-asset market orders, and only for the leg that
 * credits the order's buy asset (`refs.marketOrder.buyAssetId`) on the
 * order's chain. Funding pre-steps and any other crediting legs never appear.
 * The amount is the net ERC-20 credit in that user operation's own logs. A
 * native-asset buy (`slip44:60`: ETH on Base, BNB on BSC) emits no Transfer
 * log; its amount is the vault's own `Received(sender, amount)` log instead,
 * and `received.tokenAddress` is the native placeholder
 * `0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee`. Without that proof the order
 * publishes no fill and goes from confirming straight to complete.
 *
 * `inclusion: "preconfirmed"` means a Base Flashblocks receipt, which the
 * engine re-reads once the block seals (~2s). It is upgraded to `"block"` or
 * removed if the transaction did not land. The field is omitted (null) when
 * the operation is failed or cancelled, or parked as `settlement_delayed`.
 * A client may say "Filled" exactly while this field is non-null.
 * Completion (`state: "completed"`) still means accounting has settled.
 *
 * `received` is in raw token units of the credited token. Tokenized
 * equities need share conversion before display; `decimals`/`symbol` are
 * null unless the venue's quote named them.
 */
export const ExecutionFillSchema = lazySchema(() =>
  z.object({
    version: z.literal(1),
    actionIndex: z.number().int().nonnegative(),
    chainId: z.number().int().positive(),
    txHash: z.string().regex(/^0x[a-fA-F0-9]{64}$/u),
    userOpHash: z.string().regex(/^0x[a-fA-F0-9]{64}$/u),
    blockNumber: z.string().regex(/^(0|[1-9]\d*)$/u),
    // "preconfirmed" = Base Flashblocks receipt (sealed block hash unknown yet).
    inclusion: z.enum(["preconfirmed", "block"]),
    received: z.object({
      tokenAddress: z.string().regex(/^0x[a-f0-9]{40}$/u),
      amountRaw: PositiveIntegerAmountSchema,
      decimals: z.number().int().nonnegative().nullable(),
      symbol: z.string().min(1).nullable(),
    }),
  }),
);

export const LiveDescriptorSchema = lazySchema(() =>
  z.object({
    transport: z.literal("websocket"),
    path: z.string().min(1),
    url: z.string().min(1),
    requiresToken: z.boolean().optional(),
    token: z.string().min(1).optional(),
    expiresAt: z.string().min(1).optional(),
  }),
);

export const UserFeedLiveDescriptorSchema = lazySchema(() =>
  LiveDescriptorSchema.extend({
    userId: z.string().min(1),
  }),
);

export const PortfolioFeedLiveDescriptorSchema = lazySchema(() =>
  LiveDescriptorSchema.extend({
    portfolioId: z.string().min(1),
  }),
);

export const ExecutionOperationResponseSchema = lazySchema(() =>
  z.object({
    operationId: z.string().min(1),
    portfolioId: z.string().min(1).nullable(),
    requestKey: z.string().min(1).nullable(),
    kind: z.enum([
      "deposit",
      "execution",
      "transfer",
      "withdraw",
      "bridge",
      "converge",
    ]),
    state: ExecutionStateSchema,
    workflowId: z.string().min(1),
    workflowStatus: z.string().min(1),
    currentHeadline: z.string().nullable(),
    currentDetail: z.string().nullable(),
    currentPhase: z.string().nullable(),
    activityPhase: z.enum(EXECUTION_ACTIVITY_PHASES).optional(),
    failureCode: z
      .string()
      .regex(EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN)
      .nullable()
      .optional(),
    currentStep: z.number().nullable(),
    totalSteps: z.number().nullable(),
    fill: ExecutionFillSchema.nullable().optional(),
    output: z.unknown().nullable().optional(),
    error: z
      .object({
        name: z.string(),
        message: z.string(),
      })
      .nullable()
      .optional(),
    createdAt: z.string().min(1).optional(),
    updatedAt: z.string().min(1).optional(),
    finishedAt: z.string().nullable().optional(),
    flow: z.string().optional(),
    provider: z.string().optional(),
    deduped: z.boolean().optional(),
    queueDisposition: z.enum(["started", "deduped_existing"]).optional(),
    actionTypes: z.array(z.string()).optional(),
    completionTarget: z.enum(["submitted", "confirmed", "settled"]).optional(),
    live: LiveDescriptorSchema.optional(),
    portfolioLive: LiveDescriptorSchema.optional(),
    portfolioLives: z.array(PortfolioFeedLiveDescriptorSchema).optional(),
  }),
);

export const ExecutionRebalanceCreateResponseSchema = lazySchema(() =>
  ExecutionOperationResponseSchema.extend({
    flow: z.literal("rebalance"),
    planHash: z.string().min(1),
    waveCount: z.number().int().nonnegative(),
    previewScope: z.literal("authoritative"),
    strategyHash: z.string().min(1).optional(),
    capitalUnallocatedUsd: z.number().nullable().optional(),
  }),
);

export const ExecutionLiquidationCreateResponseSchema = lazySchema(() =>
  ExecutionOperationResponseSchema.extend({
    flow: z.literal("liquidation"),
    specHash: z.string().min(1),
  }),
);

export const ExecutionLiquidationOperationAckSchema = lazySchema(() =>
  z.object({
    operationId: z.string().min(1),
    specHash: z.string().min(1),
    workflowId: z.string().min(1).optional(),
    runId: z.string().min(1).optional(),
  }),
);

// Response-only address validation preserves historical wire casing. Request
// address normalization is owned by the shared request schemas.
const EvmAddressSchema = lazySchema(() =>
  z.string().regex(/^0x[a-fA-F0-9]{40}$/u),
);

export const OndoDirectRebalancePlanSchema = lazySchema(() =>
  z.object({
    totalValueCashRaw: NonNegativeIntegerAmountSchema,
    cashInputAmount: NonNegativeIntegerAmountSchema.nullable(),
    sells: z.array(
      z.object({
        assetId: z.string(),
        tokenAddress: EvmAddressSchema,
        amount: NonNegativeIntegerAmountSchema,
        estimatedCashOutRaw: NonNegativeIntegerAmountSchema,
        fullExit: z.boolean(),
      }),
    ),
    buys: z.array(
      z.object({
        assetId: z.string(),
        tokenAddress: EvmAddressSchema,
        cashAmount: NonNegativeIntegerAmountSchema,
      }),
    ),
    skipped: z.array(
      z.object({
        assetId: z.string(),
        side: z.enum(["sell", "buy"]),
        reason: z.string(),
        cashNotionalRaw: NonNegativeIntegerAmountSchema,
      }),
    ),
    assets: z.array(
      z.object({
        assetId: z.string(),
        currentValueCashRaw: NonNegativeIntegerAmountSchema,
        targetValueCashRaw: NonNegativeIntegerAmountSchema,
      }),
    ),
  }),
);

export const ExecutionOndoDirectRebalancePlanPreviewSchema = lazySchema(() =>
  z.object({
    flow: z.literal("ondo_direct_rebalance_plan"),
    provider: z.literal("ondo_direct"),
    portfolioId: z.string(),
    chainId: z.number().int().positive(),
    cashAssetId: z.string(),
    cashTokenAddress: EvmAddressSchema,
    vaultAddress: EvmAddressSchema,
    plan: OndoDirectRebalancePlanSchema,
  }),
);

const BebopRfqQuoteTokenSchema = lazySchema(() =>
  z
    .object({
      amount: z.string().min(1),
      amountBeforeFee: z.string().min(1).nullable().optional(),
      decimals: z.number().int().nonnegative(),
      deltaFromExpected: z.number().nullable().optional(),
      minimumAmount: z.string().min(1).optional(),
      price: z.number().nullable().optional(),
      priceBeforeFee: z.number().nullable().optional(),
      priceUsd: z.number().nullable().optional(),
      symbol: z.string().min(1),
    })
    .passthrough(),
);

const BebopRfqQuoteTokenMapSchema = lazySchema(() =>
  z.record(z.string(), BebopRfqQuoteTokenSchema),
);

const ExecutionMarketOrderQuotePreviewBaseSchema = lazySchema(() =>
  z.object({
    flow: z.literal("market_order"),
    executable: z.boolean(),
    executionUnavailableReason: z.string().min(1).nullable(),
    executionUnavailableDetail: z.string().min(1).nullable(),
    portfolioId: z.string().min(1),
    chainId: z.number().int().positive(),
    network: z.string().min(1),
    sellAssetId: z.string().min(1),
    buyAssetId: z.string().min(1),
    sellAmount: z.string().regex(/^(?:[1-9]\d*)$/u),
    vaultAddress: EvmAddressSchema,
    taker: EvmAddressSchema,
    receiver: EvmAddressSchema,
    recipient: EvmAddressSchema,
    recipientPortfolioId: z.string().min(1).nullable(),
    venue: ExpectedMarketOrderVenueSchema.optional(),
    cached: z.boolean().optional(),
    stale: z.boolean().optional(),
    quoteAgeMs: z.number().int().nonnegative().optional(),
    refreshFailedReason: z.string().min(1).nullable().optional(),
  }),
);

const ExecutionBebopMarketOrderQuotePreviewSchema = lazySchema(() =>
  ExecutionMarketOrderQuotePreviewBaseSchema.extend({
    provider: z.literal("bebop_rfq"),
    executionUnavailableReason: z
      .enum([
        "invalid_sell_amount",
        "invalid_sell_token",
        "required_signatures",
        "unsupported_approval_type",
      ])
      .nullable(),
    quote: z.object({
      quoteId: z.string().min(1),
      requestId: z.string().min(1),
      expiry: z.number().int().positive(),
      expiryType: z.enum(["standard", "short"]),
      slippage: z.number(),
      priceImpact: z.number().nullable().optional(),
      gasFee: z.object({
        native: z.string().min(1),
        usd: z.number().nullable().optional(),
      }),
      warnings: z.array(
        z.object({
          code: z.number(),
          message: z.string(),
        }),
      ),
      sellTokens: BebopRfqQuoteTokenMapSchema,
      buyTokens: BebopRfqQuoteTokenMapSchema,
      approvalType: z.enum(["Standard", "Permit", "Permit2"]),
      approvalTarget: EvmAddressSchema,
      settlementAddress: EvmAddressSchema,
    }),
  }),
);

const ExecutionBebopJamMarketOrderQuotePreviewSchema = lazySchema(() =>
  ExecutionMarketOrderQuotePreviewBaseSchema.extend({
    provider: z.literal("bebop_jam"),
    executionUnavailableReason: z
      .enum([
        "invalid_sell_amount",
        "invalid_sell_token",
        "required_signatures",
        "unsupported_approval_type",
      ])
      .nullable(),
    quote: z.object({
      quoteId: z.string().min(1),
      requestId: z.string().min(1).nullable(),
      expiry: z.number().int().positive(),
      slippage: z.number(),
      priceImpact: z.number().nullable().optional(),
      gasFee: z.object({
        native: z.string().min(1),
        usd: z.number().nullable().optional(),
      }),
      warnings: z.array(
        z.object({
          code: z.number(),
          message: z.string(),
        }),
      ),
      sellTokens: BebopRfqQuoteTokenMapSchema,
      buyTokens: BebopRfqQuoteTokenMapSchema,
      approvalType: z.enum(["Standard", "Permit", "Permit2"]),
      approvalTarget: EvmAddressSchema,
      settlementAddress: EvmAddressSchema,
      solver: z.string().min(1).nullable().optional(),
    }),
  }),
);

const ExecutionOndoDirectMarketOrderQuotePreviewSchema = lazySchema(() =>
  ExecutionMarketOrderQuotePreviewBaseSchema.extend({
    provider: z.literal("ondo_direct"),
    executionUnavailableReason: z.null(),
    executionUnavailableDetail: z.null(),
    estimatedReceiveAmount: z.string().min(1),
    estimatedReceiveAssetId: z.string().min(1),
    estimatedReceiveDecimals: z.number().int().nonnegative(),
    estimatedReceiveSymbol: z.string().min(1),
    quote: z
      .object({
        chainId: z.string().min(1),
        symbol: z.string().min(1),
        ticker: z.string().min(1),
        assetAddress: z.string().min(1),
        side: z.enum(["buy", "sell"]),
        tokenAmount: z.string().min(1),
        price: z.string().min(1),
        notionalValue: z.string().min(1),
        cashAddress: EvmAddressSchema,
        cashAssetId: z.string().min(1),
        cashSymbol: z.string().min(1),
        managerAddress: EvmAddressSchema,
      })
      .passthrough(),
  }),
);

const ExecutionLifiMarketOrderQuotePreviewSchema = lazySchema(() =>
  ExecutionMarketOrderQuotePreviewBaseSchema.extend({
    provider: z.literal("lifi"),
    venueKey: LifiMarketOrderVenueKeySchema,
    executionUnavailableReason: z.null(),
    executionUnavailableDetail: z.null(),
    estimatedReceiveAmount: z.string().min(1),
    estimatedReceiveAssetId: z.string().min(1),
    estimatedReceiveDecimals: z.number().int().nonnegative().nullable(),
    estimatedReceiveSymbol: z.string().min(1).nullable(),
    quote: z
      .object({
        routeId: z.string().min(1),
        fromChainId: z.number().int().positive(),
        toChainId: z.number().int().positive(),
        fromTokenAddress: EvmAddressSchema,
        toTokenAddress: EvmAddressSchema,
        fromAmount: z.string().min(1),
        toAmount: z.string().min(1),
        stepCount: z.number().int().positive(),
      })
      .passthrough(),
  }),
);

const ExecutionRelayMarketOrderQuotePreviewSchema = lazySchema(() =>
  ExecutionMarketOrderQuotePreviewBaseSchema.extend({
    provider: z.literal("relay"),
    executionUnavailableReason: z.null(),
    executionUnavailableDetail: z.null(),
    estimatedReceiveAmount: z.string().regex(/^[1-9]\d*$/u),
    estimatedReceiveAssetId: z.string().min(1),
    estimatedReceiveDecimals: z.number().int().nonnegative(),
    estimatedReceiveSymbol: z.string().min(1).nullable(),
    quote: z.object({
      requestId: z.string().min(1),
      topology: z.literal("atomic"),
      fromChainId: z.number().int().positive(),
      toChainId: z.number().int().positive(),
      fromTokenAddress: EvmAddressSchema,
      toTokenAddress: EvmAddressSchema,
      fromAmount: z.string().regex(/^[1-9]\d*$/u),
      toAmount: z.string().regex(/^[1-9]\d*$/u),
      minimumReceiveAmount: z.string().regex(/^[1-9]\d*$/u),
      stepCount: z.number().int().positive(),
      expiresAt: z.number().int().positive(),
    }),
  }),
);

const ExecutionRealityViaBitgetMarketOrderQuotePreviewSchema = lazySchema(() =>
  ExecutionMarketOrderQuotePreviewBaseSchema.extend({
    provider: z.literal("reality_via_bitget"),
    executionUnavailableReason: z.null(),
    executionUnavailableDetail: z.null(),
    estimatedReceiveAmount: z.string().regex(/^[1-9]\d*$/u),
    estimatedReceiveAssetId: z.string().min(1),
    estimatedReceiveDecimals: z.number().int().nonnegative(),
    estimatedReceiveSymbol: z.string().min(1),
    quote: z
      .object({
        market: z.string().min(1),
        slippage: z.string().regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/u),
        toAmount: z.string().regex(/^[1-9]\d*$/u),
      })
      .strict(),
  }),
);

/** The Solana venue that priced a quote; the executor may still fall back. */
export const SolanaMarketOrderQuoteVenueKeySchema = lazySchema(() =>
  z.enum(["jupiter", "titan", "ondo"]),
);

/**
 * A Solana swap quote from the Investing Account's Swig wallet. Amounts are
 * raw base units; `otherAmountThreshold` is the venue's guaranteed output at
 * the quoted slippage, which create freezes into the action as its floor.
 */
const ExecutionSolanaMarketOrderQuotePreviewShape = lazySchema(() =>
  ExecutionMarketOrderQuotePreviewBaseSchema.omit({
    vaultAddress: true,
    taker: true,
    receiver: true,
    recipient: true,
  }).extend({
    executionUnavailableReason: z.null(),
    executionUnavailableDetail: z.null(),
    vaultAddress: SolanaAddressSchema,
    taker: SolanaAddressSchema,
    receiver: SolanaAddressSchema,
    recipient: SolanaAddressSchema,
    venueKey: SolanaMarketOrderQuoteVenueKeySchema,
    estimatedReceiveAmount: NonNegativeIntegerAmountSchema,
    estimatedReceiveAssetId: z.string().min(1),
    estimatedReceiveDecimals: z.number().int().nonnegative().nullable(),
    estimatedReceiveSymbol: z.string().min(1).nullable(),
    quote: z
      .object({
        inputMint: SolanaAddressSchema,
        outputMint: SolanaAddressSchema,
        inAmount: PositiveIntegerAmountSchema,
        outAmount: NonNegativeIntegerAmountSchema,
        otherAmountThreshold: NonNegativeIntegerAmountSchema,
        priceImpactPct: z.string(),
        slippageBps: z.number().int().min(0).max(10_000),
      })
      .strict(),
  }),
);

const ExecutionSolanaSwapMarketOrderQuotePreviewSchema = lazySchema(() =>
  ExecutionSolanaMarketOrderQuotePreviewShape.extend({
    provider: z.literal("solana_swap"),
  }),
);

const ExecutionSolanaOndoSwapMarketOrderQuotePreviewSchema = lazySchema(() =>
  ExecutionSolanaMarketOrderQuotePreviewShape.extend({
    provider: z.literal("solana_ondo_swap"),
  }),
);

export const ExecutionMarketOrderQuotePreviewSchema = lazySchema(() =>
  z
    .discriminatedUnion("provider", [
      ExecutionBebopMarketOrderQuotePreviewSchema,
      ExecutionBebopJamMarketOrderQuotePreviewSchema,
      ExecutionOndoDirectMarketOrderQuotePreviewSchema,
      ExecutionLifiMarketOrderQuotePreviewSchema,
      ExecutionRelayMarketOrderQuotePreviewSchema,
      ExecutionRealityViaBitgetMarketOrderQuotePreviewSchema,
      ExecutionSolanaSwapMarketOrderQuotePreviewSchema,
      ExecutionSolanaOndoSwapMarketOrderQuotePreviewSchema,
    ])
    .superRefine((preview, context) => {
      if (preview.venue && preview.venue.provider !== preview.provider) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "venue provider must match quote provider",
          path: ["venue", "provider"],
        });
      }
    }),
);

/** Integrity tag on a server-issued funded-buy plan basis. */
const ExecutionSpecDrivenBuyPlanBasisIntegritySchema = lazySchema(() =>
  z.string().regex(/^sbb1\.[a-f0-9]{64}$/u),
);

/**
 * The funding plan a funded buy persisted at admission: the reviewed basis
 * plus whether create reused it and the exact trade spend it lands.
 */
export const ExecutionSpecDrivenBuyCreatedFundingSchema = lazySchema(() =>
  SpecDrivenBuyPlanBasisSchema.extend({
    integrity: ExecutionSpecDrivenBuyPlanBasisIntegritySchema.optional(),
    basisReused: z.boolean(),
    spendRaw: PositiveIntegerAmountSchema,
  }).strict(),
);

export const ExecutionOperationEventAckSchema = lazySchema(() =>
  z.object({
    operationId: z.string().min(1),
    workflowId: z.string().min(1),
    accepted: z.boolean(),
    eventType: z.string().min(1),
    state: ExecutionStateSchema,
  }),
);

const ExecutionGraphNodeSchema = lazySchema(() =>
  z.object({
    id: z.string(),
    label: z.string(),
    kind: z.string(),
    status: z.enum(["pending", "active", "completed", "failed"]).optional(),
    chainId: z.string().nullable().optional(),
    assetId: z.string().nullable().optional(),
    metadata: ExecutionLiveJsonObjectSchema.nullable().optional(),
  }),
);

const ExecutionGraphSchema: z.ZodType<OperationExecutionGraph> = lazySchema(
  () =>
    z.object({
      nodes: z.array(ExecutionGraphNodeSchema),
      edges: z.array(
        z.object({
          from: z.string(),
          to: z.string(),
        }),
      ),
    }),
);

const ExecutionPortfolioScopeSchema = lazySchema(() =>
  z.object({
    portfolioId: z.string().min(1),
    role: z.enum(["primary", "source", "destination"]),
  }),
);

const ExecutionResourceScopeSchema = lazySchema(() =>
  z.object({
    resourceKey: z.string().min(1),
    kind: z.enum(["portfolio", "asset"]),
    portfolioId: z.string().min(1),
    role: z.enum(["debit", "credit", "exclusive"]),
    chainId: z.number().int().positive().nullable().optional(),
    assetId: z.string().nullable().optional(),
  }),
);

const ExecutionTransferRefSchema = lazySchema(() =>
  z.object({
    sourcePortfolioId: z.string().min(1),
    destinationPortfolioId: z.string().min(1),
    assetId: z.string().min(1),
    amount: z.string().min(1),
    assets: z
      .array(
        z.object({
          assetId: z.string().min(1),
          amount: z.string().min(1),
        }),
      )
      .nullable()
      .optional(),
    displayAmount: z.string().nullable().optional(),
    assetSymbol: z.string().nullable().optional(),
    sourcePortfolioName: z.string().nullable().optional(),
    destinationPortfolioName: z.string().nullable().optional(),
  }),
);

const ExecutionWithdrawRefSchema = lazySchema(() =>
  z.object({
    sourcePortfolioId: z.string().min(1),
    chainId: z.number().int().positive(),
    assetId: z.string().min(1),
    amount: z.string().min(1),
    // The projection strips the raw recipient for browser-bound frames. Keep
    // this optional so snapshots written before that redaction continue to
    // parse while they drain from Durable Object storage.
    recipientAddress: z.string().min(1).optional(),
    displayAmount: z.string().nullable().optional(),
    assetSymbol: z.string().nullable().optional(),
    sourcePortfolioName: z.string().nullable().optional(),
    recipientLabel: z.string().nullable().optional(),
  }),
);

const ExecutionMarketOrderRefSchema = lazySchema(() =>
  z.object({
    flow: z.literal("market_order"),
    provider: z.enum([
      "bebop_rfq",
      "bebop_jam",
      "ondo_direct",
      "reality_via_bitget",
      "lifi",
      "relay",
      "solana_swap",
      "solana_ondo_swap",
    ]),
    intentSide: z.enum(["buy", "sell", "swap"]),
    chainId: z.number().int().positive(),
    sellAssetId: z.string().min(1),
    buyAssetId: z.string().min(1),
    sellAmount: z.string().min(1),
    cashAssetId: z.string().nullable(),
    quoteAssetId: z.string().nullable(),
  }),
);

const ExecutionBridgeRefSchema = lazySchema(() =>
  z.object({
    portfolioId: z.string().min(1),
    sourceChainId: z.number().int().positive(),
    destinationChainId: z.number().int().positive(),
    assetId: z.string().min(1),
    destinationAssetId: z.string().min(1),
    amount: z.string().min(1),
    routeProvider: z.enum(["lifi", "relay"]),
    routeId: z.string().nullable().optional(),
    bridgeTool: z.string().nullable().optional(),
    sourceVaultAddress: z.string().nullable().optional(),
    sourceTxHash: z.string().nullable().optional(),
    destinationTxHash: z.string().nullable().optional(),
    displayAmount: z.string().nullable().optional(),
    assetSymbol: z.string().nullable().optional(),
  }),
);

const ExecutionFloatRecoverySettlementRefSchema = lazySchema(() =>
  z.object({
    advanceId: z.string().min(1),
    chainRefs: z.array(z.string().min(1)).min(1),
  }),
);

/**
 * Public refs shared by the Worker and browser client. Unknown JSON-valued
 * refs survive forward-compatible readers. Raw executionIntent is stripped
 * here as defense in depth in addition to the server projection.
 */
const ExecutionLiveRefsWireShape = lazySchema(
  () =>
    ({
      workflowId: z.string().nullable().optional(),
      runId: z.string().nullable().optional(),
      eventId: z.string().nullable().optional(),
      initiatorUserId: z.string().nullable().optional(),
      capabilityRef: z
        .object({
          jti: z.string(),
          sessionRef: z.string().optional(),
        })
        .nullable()
        .optional(),
      legacyOperationId: z.string().nullable().optional(),
      requestKey: z.string().nullable().optional(),
      activityFailureCode: z
        .string()
        .regex(EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN)
        .nullable()
        .optional(),
      correlationId: z.string().nullable().optional(),
      scheduleId: z.string().nullable().optional(),
      scheduledFunctionId: z.string().nullable().optional(),
      scheduledRunAt: z.string().nullable().optional(),
      requestId: z.string().nullable().optional(),
      executionSource: z
        .enum(["manual", "scheduled", "event", "scheduled_function"])
        .nullable()
        .optional(),
      portfolioScopes: z
        .array(ExecutionPortfolioScopeSchema)
        .nullable()
        .optional(),
      resourceScopes: z
        .array(ExecutionResourceScopeSchema)
        .nullable()
        .optional(),
      transfer: ExecutionTransferRefSchema.nullable().optional(),
      withdraw: ExecutionWithdrawRefSchema.nullable().optional(),
      marketOrder: ExecutionMarketOrderRefSchema.nullable().optional(),
      bridge: ExecutionBridgeRefSchema.nullable().optional(),
      floatRecoverySettlement:
        ExecutionFloatRecoverySettlementRefSchema.nullable().optional(),
      // Explicitly admit then remove these internal fields before the catchall so
      // forward-compatible refs cannot bypass the browser redaction boundary.
      executionIntent: z.unknown().optional(),
      workflowParams: z.unknown().optional(),
      workflowVersion: z.unknown().optional(),
    }) satisfies z.ZodRawShape,
);

const ExecutionLiveRefsWireSchema = lazySchema(() =>
  z
    .object({ ...ExecutionLiveRefsWireShape })
    .catchall(ExecutionLiveJsonValueSchema),
);

export const ExecutionLiveRefsSchema = lazySchema(() =>
  ExecutionLiveRefsWireSchema.transform((refs): ExecutionLiveRefs => {
    const {
      executionIntent: _executionIntent,
      workflowParams: _workflowParams,
      workflowVersion: _workflowVersion,
      ...publicRefs
    } = refs;
    return publicRefs;
  }),
);

export const ExecutionLivePublishRefsSchema = lazySchema(() =>
  z
    .object({ ...ExecutionLiveRefsWireShape })
    .transform((refs): ExecutionLiveRefs => {
      const {
        executionIntent: _executionIntent,
        workflowParams: _workflowParams,
        workflowVersion: _workflowVersion,
        ...publicRefs
      } = refs;
      return publicRefs;
    }),
);

export const ExecutionLiveOperationSchema = lazySchema(() =>
  z.object({
    operationId: z.string(),
    // Optional for snapshots persisted by pre-revision Durable Objects. New
    // engine projections always emit the decimal monotonic source revision.
    sourceRevision: z
      .string()
      .regex(/^(0|[1-9]\d*)$/)
      .optional(),
    runId: z.string(),
    portfolioId: z.string(),
    kind: z.enum([
      "rebalance",
      "deposit",
      "withdraw",
      "transfer",
      "bridge",
      "swap",
      "execution",
      "converge",
    ]),
    state: ExecutionStateSchema,
    title: z.string(),
    currentHeadline: z.string().nullable(),
    currentDetail: z.string().nullable(),
    currentPhase: z.string().nullable(),
    // Optional while old Durable Object snapshots drain. New projections always
    // emit the stable semantic phase; currentPhase remains for old clients.
    activityPhase: z.enum(EXECUTION_ACTIVITY_PHASES).optional(),
    failureCode: z
      .string()
      .regex(EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN)
      .nullable()
      .optional(),
    currentStep: z.number().int().nullable(),
    totalSteps: z.number().int().nullable(),
    // Optional while old Durable Object snapshots drain; see ExecutionFillSchema.
    fill: ExecutionFillSchema.nullable().optional(),
    refs: ExecutionLiveRefsSchema,
    viewKind: z.enum(["timeline", "observer_graph"]),
    graph: ExecutionGraphSchema.nullable(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
);

export const ExecutionLiveTimelineEventSchema = lazySchema(() =>
  z.object({
    id: z.string(),
    operationId: z.string(),
    runId: z.string(),
    sequence: z.number().int().nonnegative(),
    dedupeKey: z.string(),
    kind: z.string(),
    eventKind: z.enum(EXECUTION_ACTIVITY_EVENT_KINDS).optional(),
    activityPhase: z.enum(EXECUTION_ACTIVITY_PHASES).optional(),
    failureCode: z
      .string()
      .regex(EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN)
      .nullable()
      .optional(),
    title: z.string(),
    detail: z.string().nullable(),
    phase: z.string().nullable(),
    status: z
      .enum([
        "pending",
        "active",
        "waiting",
        "blocked",
        "completed",
        "failed",
        "info",
      ])
      .nullable(),
    groupKey: z.string().nullable(),
    parentGroupKey: z.string().nullable(),
    laneKey: z.string().nullable(),
    refs: ExecutionLiveJsonObjectSchema.nullable(),
    data: ExecutionLiveJsonObjectSchema.nullable(),
    createdAt: z.string(),
  }),
);

export const ExecutionLiveSnapshotSchema = lazySchema(() =>
  z.object({
    kind: z.literal("execution.snapshot"),
    operation: ExecutionLiveOperationSchema,
    events: z.array(ExecutionLiveTimelineEventSchema),
    sequence: z.number().int().nonnegative(),
    ts: z.string(),
  }),
);

export const ExecutionLiveEventSchema = lazySchema(() =>
  z.object({
    kind: z.literal("execution.event"),
    event: ExecutionLiveTimelineEventSchema,
    operation: ExecutionLiveOperationSchema,
    sequence: z.number().int().nonnegative(),
    ts: z.string(),
  }),
);

export const ExecutionLiveProjectionSchema = lazySchema(() =>
  z.object({
    snapshot: ExecutionLiveSnapshotSchema,
    event: ExecutionLiveEventSchema.optional(),
  }),
);

/** Builds on first call, then returns the same value (module-load stays cheap). */
const once = /* @__NO_SIDE_EFFECTS__ */ <T>(build: () => T): (() => T) => {
  let built: { readonly value: T } | undefined;
  return () => (built ??= { value: build() }).value;
};

// Shared by several unions below; built once so they share option instances.
const LiveClientControlSchemas = once(
  () =>
    [
      z.object({
        kind: z.literal("hello"),
        lastSequence: z.number().int().nonnegative().optional(),
      }),
      // Reserved for future backpressure/retention tuning. Durable Objects accept
      // and ignore it today so newer clients can safely send optimistic acks.
      z.object({
        kind: z.literal("ack"),
        sequence: z.number().int().nonnegative(),
      }),
      z.object({
        kind: z.literal("ping"),
        clientTs: z.number().finite(),
      }),
    ] as const,
);

export const ExecutionLiveClientMessageSchema = lazySchema(() =>
  z.discriminatedUnion("kind", LiveClientControlSchemas()),
);

const LiveServerControlSchemas = once(
  () =>
    [
      z.object({
        kind: z.literal("pong"),
        clientTs: z.number().finite(),
        serverTs: z.number().finite(),
      }),
      z.object({
        kind: z.literal("error"),
        code: z.string(),
        message: z.string(),
      }),
    ] as const,
);

export const ExecutionLiveServerMessageSchema = lazySchema(() =>
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: ExecutionLiveSnapshotSchema,
    }),
    z.object({
      kind: z.literal("event"),
      sequence: z.number().int().nonnegative(),
      payload: ExecutionLiveEventSchema,
    }),
    ...LiveServerControlSchemas(),
  ]),
);

export const PortfolioExecutionFeedSnapshotSchema = lazySchema(() =>
  z.object({
    kind: z.literal("portfolio.execution_feed.snapshot"),
    portfolioId: z.string().min(1),
    operations: z.array(ExecutionLiveOperationSchema),
    sequence: z.number().int().nonnegative(),
    ts: z.string(),
  }),
);

export const PortfolioExecutionFeedOperationSchema = lazySchema(() =>
  z.object({
    kind: z.literal("portfolio.execution_feed.operation"),
    portfolioId: z.string().min(1),
    operation: ExecutionLiveOperationSchema,
    sequence: z.number().int().nonnegative(),
    ts: z.string(),
  }),
);

export const PortfolioExecutionFeedProjectionSchema = lazySchema(() =>
  z.object({
    snapshot: PortfolioExecutionFeedSnapshotSchema,
    operation: PortfolioExecutionFeedOperationSchema.optional(),
  }),
);

export const PortfolioExecutionFeedHydrateInputSchema = lazySchema(() =>
  z.object({
    portfolioId: z.string().min(1),
    operations: z.array(ExecutionLiveOperationSchema),
    expectedSequence: z.number().int().nonnegative().optional(),
  }),
);

export const PortfolioExecutionFeedClientMessageSchema = lazySchema(() =>
  z.discriminatedUnion("kind", LiveClientControlSchemas()),
);

export const PortfolioExecutionFeedServerMessageSchema = lazySchema(() =>
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: PortfolioExecutionFeedSnapshotSchema,
    }),
    z.object({
      kind: z.literal("operation"),
      sequence: z.number().int().nonnegative(),
      payload: PortfolioExecutionFeedOperationSchema,
    }),
    ...LiveServerControlSchemas(),
  ]),
);

export const UserExecutionFeedSnapshotSchema = lazySchema(() =>
  z.object({
    kind: z.literal("user.execution_feed.snapshot"),
    userId: z.string().min(1),
    operations: z.array(ExecutionLiveOperationSchema),
    sequence: z.number().int().nonnegative(),
    ts: z.string(),
  }),
);

export const UserExecutionFeedOperationSchema = lazySchema(() =>
  z.object({
    kind: z.literal("user.execution_feed.operation"),
    userId: z.string().min(1),
    operation: ExecutionLiveOperationSchema,
    sequence: z.number().int().nonnegative(),
    ts: z.string(),
  }),
);

export const UserExecutionFeedProjectionSchema = lazySchema(() =>
  z.object({
    snapshot: UserExecutionFeedSnapshotSchema,
    operation: UserExecutionFeedOperationSchema.optional(),
  }),
);

export const UserExecutionFeedHydrateInputSchema = lazySchema(() =>
  z.object({
    userId: z.string().min(1),
    operations: z.array(ExecutionLiveOperationSchema),
    expectedSequence: z.number().int().nonnegative().optional(),
  }),
);

export const UserExecutionFeedClientMessageSchema = lazySchema(() =>
  z.discriminatedUnion("kind", LiveClientControlSchemas()),
);

export const UserExecutionFeedServerMessageSchema = lazySchema(() =>
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: UserExecutionFeedSnapshotSchema,
    }),
    z.object({
      kind: z.literal("operation"),
      sequence: z.number().int().nonnegative(),
      payload: UserExecutionFeedOperationSchema,
    }),
    ...LiveServerControlSchemas(),
  ]),
);

export const ExecutionLivePublishOperationSchema = lazySchema(() =>
  ExecutionLiveOperationSchema.extend({ refs: ExecutionLivePublishRefsSchema }),
);

export const ExecutionLivePublishSnapshotSchema = lazySchema(() =>
  ExecutionLiveSnapshotSchema.extend({
    operation: ExecutionLivePublishOperationSchema,
  }),
);

export const ExecutionLivePublishEventSchema = lazySchema(() =>
  ExecutionLiveEventSchema.extend({
    operation: ExecutionLivePublishOperationSchema,
  }),
);

export const ExecutionLivePublishProjectionSchema = lazySchema(() =>
  z.object({
    snapshot: ExecutionLivePublishSnapshotSchema,
    event: ExecutionLivePublishEventSchema.optional(),
  }),
);

export const ExecutionLivePublishServerMessageSchema = lazySchema(() =>
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: ExecutionLivePublishSnapshotSchema,
    }),
    z.object({
      kind: z.literal("event"),
      sequence: z.number().int().nonnegative(),
      payload: ExecutionLivePublishEventSchema,
    }),
    ...LiveServerControlSchemas(),
  ]),
);

export const PortfolioExecutionFeedPublishSnapshotSchema = lazySchema(() =>
  PortfolioExecutionFeedSnapshotSchema.extend({
    operations: z.array(ExecutionLivePublishOperationSchema),
  }),
);

export const PortfolioExecutionFeedPublishOperationSchema = lazySchema(() =>
  PortfolioExecutionFeedOperationSchema.extend({
    operation: ExecutionLivePublishOperationSchema,
  }),
);

export const PortfolioExecutionFeedPublishProjectionSchema = lazySchema(() =>
  z.object({
    snapshot: PortfolioExecutionFeedPublishSnapshotSchema,
    operation: PortfolioExecutionFeedPublishOperationSchema.optional(),
  }),
);

export const PortfolioExecutionFeedPublishHydrateInputSchema = lazySchema(() =>
  PortfolioExecutionFeedHydrateInputSchema.extend({
    operations: z.array(ExecutionLivePublishOperationSchema),
  }),
);

export const PortfolioExecutionFeedPublishServerMessageSchema = lazySchema(() =>
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: PortfolioExecutionFeedPublishSnapshotSchema,
    }),
    z.object({
      kind: z.literal("operation"),
      sequence: z.number().int().nonnegative(),
      payload: PortfolioExecutionFeedPublishOperationSchema,
    }),
    ...LiveServerControlSchemas(),
  ]),
);

export const UserExecutionFeedPublishSnapshotSchema = lazySchema(() =>
  UserExecutionFeedSnapshotSchema.extend({
    operations: z.array(ExecutionLivePublishOperationSchema),
  }),
);

export const UserExecutionFeedPublishOperationSchema = lazySchema(() =>
  UserExecutionFeedOperationSchema.extend({
    operation: ExecutionLivePublishOperationSchema,
  }),
);

export const UserExecutionFeedPublishProjectionSchema = lazySchema(() =>
  z.object({
    snapshot: UserExecutionFeedPublishSnapshotSchema,
    operation: UserExecutionFeedPublishOperationSchema.optional(),
  }),
);

export const UserExecutionFeedPublishHydrateInputSchema = lazySchema(() =>
  UserExecutionFeedHydrateInputSchema.extend({
    operations: z.array(ExecutionLivePublishOperationSchema),
  }),
);

export const UserExecutionFeedPublishServerMessageSchema = lazySchema(() =>
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("snapshot"),
      sequence: z.number().int().nonnegative(),
      payload: UserExecutionFeedPublishSnapshotSchema,
    }),
    z.object({
      kind: z.literal("operation"),
      sequence: z.number().int().nonnegative(),
      payload: UserExecutionFeedPublishOperationSchema,
    }),
    ...LiveServerControlSchemas(),
  ]),
);

export type ExecutionOperationResponse = z.infer<
  typeof ExecutionOperationResponseSchema
>;
export type ExecutionRebalanceCreateResponse = z.infer<
  typeof ExecutionRebalanceCreateResponseSchema
>;
export type ExecutionLiquidationCreateResponse = z.infer<
  typeof ExecutionLiquidationCreateResponseSchema
>;
export type ExecutionLiquidationOperationAck = z.infer<
  typeof ExecutionLiquidationOperationAckSchema
>;
export type ExecutionFill = z.infer<typeof ExecutionFillSchema>;
export type LiveDescriptor = z.infer<typeof LiveDescriptorSchema>;
export type UserFeedLiveDescriptor = z.infer<
  typeof UserFeedLiveDescriptorSchema
>;
export type PortfolioFeedLiveDescriptor = z.infer<
  typeof PortfolioFeedLiveDescriptorSchema
>;
export type ExecutionOperationEventAck = z.infer<
  typeof ExecutionOperationEventAckSchema
>;
export type ExecutionLiveOperation = z.infer<
  typeof ExecutionLiveOperationSchema
>;
export type ExecutionLiveTimelineEvent = z.infer<
  typeof ExecutionLiveTimelineEventSchema
>;
export type ExecutionLiveSnapshot = z.infer<typeof ExecutionLiveSnapshotSchema>;
export type ExecutionLiveEvent = z.infer<typeof ExecutionLiveEventSchema>;
export type ExecutionLiveProjection = z.infer<
  typeof ExecutionLiveProjectionSchema
>;
export type ExecutionLiveClientMessage = z.infer<
  typeof ExecutionLiveClientMessageSchema
>;
export type ExecutionLiveServerMessage = z.infer<
  typeof ExecutionLiveServerMessageSchema
>;
export type PortfolioExecutionFeedSnapshot = z.infer<
  typeof PortfolioExecutionFeedSnapshotSchema
>;
export type PortfolioExecutionFeedOperation = z.infer<
  typeof PortfolioExecutionFeedOperationSchema
>;
export type PortfolioExecutionFeedProjection = z.infer<
  typeof PortfolioExecutionFeedProjectionSchema
>;
export type PortfolioExecutionFeedHydrateInput = z.infer<
  typeof PortfolioExecutionFeedHydrateInputSchema
>;
export type PortfolioExecutionFeedClientMessage = z.infer<
  typeof PortfolioExecutionFeedClientMessageSchema
>;
export type PortfolioExecutionFeedServerMessage = z.infer<
  typeof PortfolioExecutionFeedServerMessageSchema
>;
export type UserExecutionFeedSnapshot = z.infer<
  typeof UserExecutionFeedSnapshotSchema
>;
export type UserExecutionFeedOperation = z.infer<
  typeof UserExecutionFeedOperationSchema
>;
export type UserExecutionFeedProjection = z.infer<
  typeof UserExecutionFeedProjectionSchema
>;
export type UserExecutionFeedHydrateInput = z.infer<
  typeof UserExecutionFeedHydrateInputSchema
>;
export type UserExecutionFeedClientMessage = z.infer<
  typeof UserExecutionFeedClientMessageSchema
>;
export type UserExecutionFeedServerMessage = z.infer<
  typeof UserExecutionFeedServerMessageSchema
>;

export type ExecutionLiveOperationRuntimeTypes = {
  readonly kind: OperationExecutionKind;
  readonly state: OperationExecutionState;
  readonly refs: ExecutionLiveRefs;
  readonly viewKind: OperationExecutionViewKind;
  readonly graph: OperationExecutionGraph | null;
  readonly eventStatus: OperationExecutionEventStatus | null;
};
